// =============================================
// MediPoints API Service for MediCureOn
// Enterprise-grade centralized MediPoints management
// Handles all MediPoints transactions, balance queries, and history
// =============================================

import { apiConfig, getRegionFromCountry } from '../config/apiConfig';

class MediPointsApiService {
  constructor() {
    this.baseUrl = apiConfig.backendUrl;
    this.functionKey = apiConfig.functionKeys.mediPointsManager;
    this.endpoint = '/api/medi-points-manager';
  }

  // Helper method to get headers with authentication and region info
  getHeaders(userCountry = 'United States', authToken = null, subscriptionTier = null) {
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Country': userCountry
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    if (subscriptionTier) {
      headers['X-User-Subscription-Tier'] = subscriptionTier;
    }
    
    return headers;
  }

  // Helper method to build URL with function key and country parameter
  buildUrl(queryParams = {}, userCountry = 'United States') {
    const url = new URL(`${this.baseUrl}${this.endpoint}`);
    url.searchParams.append('code', this.functionKey);
    
    // Add country as query parameter to match profile function pattern
    url.searchParams.append('country', userCountry);
    
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.append(key, value.toString());
      }
    });
    
    return url.toString();
  }

  // Test MediPoints service connectivity
  async testConnection(userId, userCountry = 'United States', subscriptionTier = null) {
    try {
      console.log('🔍 Testing MediPoints service connection...');
      const url = this.buildUrl({ action: 'balance', userId }, userCountry);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, null, subscriptionTier)
      });
      
      if (!response.ok) {
        throw new Error(`MediPoints service test failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ MediPoints service connection successful:', data);
      return { success: true, data };
    } catch (error) {
      console.error('❌ MediPoints service connection failed:', error);
      throw error;
    }
  }

  // Get user's current MediPoints balance and comprehensive data
  async getBalance(userId, userCountry = 'United States', authToken = null, subscriptionTier = null) {
    try {
      console.log(`💰 Fetching MediPoints balance for user: ${userId}`);
      const url = this.buildUrl({ action: 'balance', userId }, userCountry);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch balance: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('💰 Enterprise Balance Response:', result);
      
      // 🌟 ENTERPRISE RESPONSE FORMAT: Handle new comprehensive balance structure
      const balanceData = result.data || {};
      
      return {
        success: true,
        balance: {
          // Core balance metrics (Enterprise V3.0 format)
          currentBalance: balanceData.currentBalance || 0,
          totalEarned: balanceData.totalEarned || 0,
          totalSpent: balanceData.totalSpent || 0,
          totalRedeemed: balanceData.totalRedeemed || 0,
          tier: balanceData.tier || 'Bronze',
          pointsToNextTier: balanceData.pointsToNextTier || 500,
          lastUpdated: balanceData.lastUpdated || new Date().toISOString(),
          transactionCount: balanceData.transactionCount || 0,
          
          // 🎯 SOURCE BREAKDOWN (Production Analytics)
          sourceSummary: balanceData.sourceSummary || {
            dailyActivities: 0,
            challenges: 0,
            bonuses: 0,
            completedChallenges: 0,
            streakDays: 0
          },
          
          // 🏆 ACHIEVEMENTS SUMMARY
          achievements: balanceData.achievements || {
            totalBadges: 0,
            totalMilestones: 0,
            latestAchievement: null
          },
          
          // 📊 ENGAGEMENT METRICS
          engagement: balanceData.engagement || {
            consistencyScore: 0,
            diversityScore: 0,
            platformEngagementScore: 0,
            disengagementRisk: 'low'
          },
          
          // 🎁 REDEMPTION INFO
          redemptionSummary: balanceData.redemptionSummary || {
            totalRedeemed: 0,
            redemptionCount: 0,
            lastRedemption: null,
            recommendedRedemptionTiming: 'save_more'
          },
          
          // Regional information
          region: result.region || 'US'
        }
      };
    } catch (error) {
      console.error('❌ Error fetching MediPoints balance:', error);
      return {
        success: false,
        balance: {
          currentBalance: 0,
          totalEarned: 0,
          totalSpent: 0,
          tier: 'Bronze',
          pointsToNextTier: 500,
          lastUpdated: new Date().toISOString()
        },
        error: error.message
      };
    }
  }

  // Add MediPoints to user account
  async addPoints(userId, amount, source, description = '', metadata = {}, userCountry = 'United States', authToken = null, subscriptionTier = null) {
    try {
      console.log(`➕ Adding ${amount} MediPoints for user: ${userId}`);
      console.log(`📝 Source: ${source}, Description: ${description}`);
      
      const requestBody = {
        action: 'add',
        userId,
        amount: parseInt(amount),
        source,
        description,
        userRegion: getRegionFromCountry(userCountry),
        metadata: {
          timestamp: new Date().toISOString(),
          ...metadata
        }
      };
      
      if (subscriptionTier) {
        requestBody.subscriptionTier = subscriptionTier;
      }
      
      const response = await fetch(this.buildUrl({}, userCountry), {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier),
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add points: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Points added successfully:', result.data);
      
      // Handle both enterprise and simple response formats
      const newBalance = result.data?.balance?.currentBalance || result.data?.newBalance || 0;
      const transaction = result.data?.transaction || {};
      
      return {
        success: true,
        data: result.data,
        newBalance: newBalance,
        transaction: transaction
      };
    } catch (error) {
      console.error('❌ Error adding MediPoints:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Deduct MediPoints from user account
  async deductPoints(userId, amount, reason, description = '', userCountry = 'United States', authToken = null) {
    try {
      console.log(`➖ Deducting ${amount} MediPoints for user: ${userId}`);
      console.log(`📝 Reason: ${reason}, Description: ${description}`);
      
      const requestBody = {
        action: 'deduct',
        userId,
        amount: parseInt(amount),
        source: reason,
        description,
        userRegion: getRegionFromCountry(userCountry),
        metadata: {
          timestamp: new Date().toISOString(),
          type: 'deduction'
        }
      };
      
      const response = await fetch(this.buildUrl({}, userCountry), {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken),
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to deduct points: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Points deducted successfully:', result.data);
      
      // Handle both enterprise and simple response formats
      const newBalance = result.data?.balance?.currentBalance || result.data?.newBalance || 0;
      const transaction = result.data?.transaction || {};
      
      return {
        success: true,
        data: result.data,
        newBalance: newBalance,
        transaction: transaction
      };
    } catch (error) {
      console.error('❌ Error deducting MediPoints:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get comprehensive MediPoints transaction history
  async getHistory(userId, limit = 50, userCountry = 'United States', authToken = null, subscriptionTier = null) {
    try {
      console.log(`📜 Fetching MediPoints history for user: ${userId}`);
      const url = this.buildUrl({ action: 'history', userId, limit });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch history: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('📜 History fetched successfully:', result.data);
      
      // Handle both enterprise and simple response formats
      const historyData = result.data?.history || result.data?.transactions || [];
      const totalCount = result.data?.totalCount || result.data?.totalTransactions || 0;
      
      return {
        success: true,
        history: historyData,
        totalTransactions: totalCount
      };
    } catch (error) {
      console.error('❌ Error fetching MediPoints history:', error);
      return {
        success: false,
        history: [],
        totalTransactions: 0,
        error: error.message
      };
    }
  }

  // Get comprehensive user statistics
  async getStats(userId, userCountry = 'United States', authToken = null) {
    try {
      console.log(`📊 Fetching MediPoints statistics for user: ${userId}`);
      const url = this.buildUrl({ action: 'stats', userId });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch stats: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('📊 Statistics fetched successfully:', result.data);
      
      return {
        success: true,
        stats: result.data || {}
      };
    } catch (error) {
      console.error('❌ Error fetching MediPoints statistics:', error);
      return {
        success: false,
        stats: {},
        error: error.message
      };
    }
  }

  // Save comprehensive MediPoints data (bulk operation)
  async saveMediPointsData(userId, mediPointsData, userCountry = 'United States', authToken = null) {
    try {
      console.log(`💾 Saving comprehensive MediPoints data for user: ${userId}`);
      console.log(`📁 Data being saved:`, mediPointsData);
      
      const requestBody = {
        action: 'save',
        userId,
        mediPointsData: {
          ...mediPointsData,
          lastUpdated: new Date().toISOString(),
          region: getRegionFromCountry(userCountry)
        },
        userRegion: getRegionFromCountry(userCountry)
      };
      
      const response = await fetch(this.buildUrl(), {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken),
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save MediPoints data: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('💾 MediPoints data saved successfully:', result.data);
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error saving MediPoints data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get comprehensive MediPoints data (bulk operation)
  async getMediPointsData(userId, userCountry = 'United States', authToken = null) {
    try {
      console.log(`📁 Fetching comprehensive MediPoints data for user: ${userId}`);
      const url = this.buildUrl({ action: 'get', userId });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch MediPoints data: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('📁 MediPoints data fetched successfully:', result.data);
      
      return {
        success: true,
        data: result.data || {
          currentBalance: 0,
          totalEarned: 0,
          totalSpent: 0,
          tier: 'Bronze',
          pointsToNextTier: 500,
          transactions: [],
          lastUpdated: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('❌ Error fetching comprehensive MediPoints data:', error);
      return {
        success: false,
        data: {
          currentBalance: 0,
          totalEarned: 0,
          totalSpent: 0,
          tier: 'Bronze',
          pointsToNextTier: 500,
          transactions: [],
          lastUpdated: new Date().toISOString()
        },
        error: error.message
      };
    }
  }

  // Redeem points for rewards
  async redeemPoints(userId, rewardId, userCountry = 'United States', authToken = null) {
    try {
      console.log(`🎁 Redeeming points for reward: ${rewardId}, User: ${userId}`);
      
      const requestBody = {
        action: 'redeem',
        userId,
        rewardId,
        userRegion: getRegionFromCountry(userCountry)
      };
      
      const response = await fetch(this.buildUrl(), {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken),
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to redeem points: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('🎁 Points redeemed successfully:', result.data);
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error redeeming points:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get rewards catalog
  async getRewardsCatalog(userCountry = 'United States') {
    try {
      console.log('🎁 Fetching rewards catalog...');
      const url = this.buildUrl({ action: 'rewards_catalog' });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch rewards catalog: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('🎁 Rewards catalog fetched successfully:', result.data);
      
      return {
        success: true,
        rewards: result.data?.rewards || []
      };
    } catch (error) {
      console.error('❌ Error fetching rewards catalog:', error);
      return {
        success: false,
        rewards: [],
        error: error.message
      };
    }
  }

  // 🌟 ENTERPRISE METHOD: Add points with enhanced metadata for daily activities
  async addPointsForDailyActivity(userId, amount, activityType, dataType, metadata = {}, userCountry = 'United States', authToken = null, subscriptionTier = null) {
    try {
      console.log(`🎯 Adding ${amount} points for daily activity: ${activityType}`);
      
      const enhancedMetadata = {
        activityType,
        dataType,
        timestamp: new Date().toISOString(),
        source: 'EarnPointsTab',
        category: 'dailyActivities',
        ...metadata
      };
      
      return await this.addPoints(
        userId, 
        amount, 
        'Daily Health Activity', 
        `Completed ${activityType}`, 
        enhancedMetadata, 
        userCountry, 
        authToken,
        subscriptionTier
      );
    } catch (error) {
      console.error('❌ Error adding points for daily activity:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 🌟 ENTERPRISE METHOD: Add points for challenge completion
  async addPointsForChallengeCompletion(userId, amount, challengeId, challengeType, metadata = {}, userCountry = 'United States', authToken = null) {
    try {
      console.log(`🏆 Adding ${amount} points for challenge completion: ${challengeId}`);
      
      const enhancedMetadata = {
        challengeId,
        challengeType,
        challengeCompleted: true,
        timestamp: new Date().toISOString(),
        source: 'ChallengesTab',
        category: 'challenges',
        ...metadata
      };
      
      return await this.addPoints(
        userId, 
        amount, 
        'Challenge Completion', 
        `Completed challenge: ${challengeId}`, 
        enhancedMetadata, 
        userCountry, 
        authToken
      );
    } catch (error) {
      console.error('❌ Error adding points for challenge completion:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get today's claimed daily activities
  async getDailyClaimedActivities(userId, userCountry = 'United States', authToken = null, subscriptionTier = null) {
    try {
      console.log(`📅 Fetching today's claimed activities for user: ${userId}`);
      const url = this.buildUrl({ action: 'daily_claims', userId }, userCountry);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch daily claims: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('📅 Daily claims fetched:', result.data);
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching daily claims:', error);
      return {
        success: false,
        error: error.message,
        data: { claimedActivities: [], totalClaims: 0 }
      };
    }
  }

  async getAnalyticsDashboard(userId, userCountry, authToken) {
    try {
      console.log(`📊 Fetching analytics dashboard for user: ${userId}`);
      const url = this.buildUrl({ action: 'analytics', userId });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch analytics: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('📊 Analytics dashboard fetched:', result.data);
      
      return {
        success: true,
        analytics: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching analytics dashboard:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get leaderboard
  async getLeaderboard(userCountry = 'United States', limit = 100) {
    try {
      console.log('🏆 Fetching MediPoints leaderboard...');
      const url = this.buildUrl({ action: 'leaderboard', limit });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch leaderboard: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('🏆 Leaderboard fetched successfully:', result.data);
      
      return {
        success: true,
        leaderboard: result.data?.leaderboard || []
      };
    } catch (error) {
      console.error('❌ Error fetching leaderboard:', error);
      return {
        success: false,
        leaderboard: [],
        error: error.message
      };
    }
  }

  // Check if user is eligible for a specific bonus award
  async checkBonusEligibility(userId, bonusType, userCountry = 'United States', authToken = null) {
    try {
      console.log(`🔍 Checking bonus eligibility for ${bonusType}: ${userId}`);
      const url = this.buildUrl({ action: 'bonus_check', userId, bonusType }, userCountry);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to check bonus eligibility: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log(`🔍 Bonus eligibility check for ${bonusType}:`, result.data);
      
      return {
        success: true,
        data: {
          alreadyClaimed: result.data?.alreadyClaimed || false,
          eligible: result.data?.eligible || false,
          filesExist: result.data?.filesExist || false,
          fileDetails: result.data?.fileDetails || {},
          lastClaimedDate: result.data?.lastClaimedDate || null,
          reason: result.data?.reason || 'Unknown'
        }
      };
    } catch (error) {
      console.error(`❌ Error checking bonus eligibility for ${bonusType}:`, error);
      return {
        success: false,
        data: { alreadyClaimed: false, eligible: true },
        error: error.message
      };
    }
  }

  // Clean up duplicate transactions (admin function)
  async cleanupDuplicates(userId, userCountry = 'United States', authToken = null) {
    try {
      console.log(`🧹 Cleaning up duplicate transactions for user: ${userId}`);
      const url = this.buildUrl({ action: 'cleanup_duplicates', userId }, userCountry);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to cleanup duplicates: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log(`🧹 Cleanup result:`, result.data);
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error(`❌ Error cleaning up duplicates:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance
const medipointsApiService = new MediPointsApiService();
export default medipointsApiService;

// Export class for testing or multiple instances if needed
export { MediPointsApiService };
